import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  CircleDot,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  PersonStanding,
  Plus,
  Sparkles,
  User,
  Zap,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/prp-therapy-hair-loss-mumbai";
const HAIR_TRANSPLANT_PATH = "/hair-transplant-mumbai";
const BEARD_TRANSPLANT_PATH = "/beard-transplant-mumbai";

const PRP_FAQS = [
  {
    question: "How many PRP sessions are needed for hair loss?",
    answer:
      "The standard initial protocol is 3 to 4 sessions spaced one month apart. After the initial course, maintenance sessions every 4 to 6 months sustain the improvement. The total number of sessions depends on the degree of thinning, the underlying cause of hair loss, and how well the individual patient responds. Most patients continue maintenance sessions indefinitely to sustain results alongside ongoing hair loss progression.",
  },
  {
    question: "Can PRP regrow hair in completely bald areas?",
    answer:
      "No. PRP stimulates existing follicles. In areas where follicles have been permanently lost, there is no follicle to stimulate and PRP produces no meaningful hair growth. PRP is effective for areas of thinning where follicles are still present but underperforming. Patients with established bald areas need hair transplant to restore growth in those zones.",
  },
  {
    question: "How long before I see results from PRP?",
    answer:
      "Reduced hair fall is typically the first change, often noticeable after the first or second session. Visible improvement in density and hair thickness develops from months 3 to 6. Maximum improvement from the initial course is visible at 6 to 12 months. Results build progressively and are not immediate.",
  },
  {
    question: "Is PRP therapy safe?",
    answer:
      "Yes. PRP uses the patient's own blood so there is no risk of allergic reaction or rejection. The preparation and injection process carries the standard minimal risks of any injection: temporary redness, mild swelling, and brief discomfort at injection sites. These resolve within 24 hours. Serious complications are rare.",
  },
  {
    question: "Can PRP be combined with hair transplant?",
    answer:
      "Yes, and this is a recommended combination. PRP applied at the time of transplant improves graft survival by increasing growth factor concentration in the recipient tissue during the critical early healing phase. Post-transplant PRP sessions maintain native surrounding hair density and support the long-term result. The combination consistently produces better overall density than transplant alone.",
  },
  {
    question: "Does PRP work for female hair loss?",
    answer:
      "Yes. Female pattern hair loss, characterized by diffuse scalp thinning and a widening parting, often responds well to PRP because follicles are present across the scalp and accessible to the growth factor stimulus. PRP reduces shedding, improves hair caliber, and slows progression in women with androgenetic alopecia, telogen effluvium, and other non-scarring hair loss conditions.",
  },
  {
    question: "What is the difference between PRP and Minoxidil?",
    answer:
      "Minoxidil is a topical medication that prolongs the hair follicle's growth phase and is applied daily. It requires continuous use to maintain results and stops working when discontinued. PRP is an injectable treatment applied in sessions that stimulates growth factor activity in the follicle and scalp tissue. Both address existing follicles rather than creating new ones. They work through different mechanisms and are frequently used together as complementary treatments for comprehensive hair loss management.",
  },
  {
    question: "Can PRP be done on the beard or eyebrows?",
    answer:
      "Yes. PRP injected into the beard zone improves density and hair caliber in patchy beard areas where follicles are present. PRP into the eyebrow area improves thickness in thinning eyebrows. The technique and session protocol are the same as for scalp PRP. For patients with absent beard or eyebrow follicles, PRP alone is not sufficient and transplant is the appropriate treatment.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalTherapy",
      name: "PRP therapy for hair loss",
      description:
        "Non-surgical platelet-rich plasma injections to the scalp to support existing hair follicles, reduce shedding, and improve hair caliber; used alone in early thinning or with hair transplant as an adjunct.",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Scalp" },
      url: PAGE_URL,
      medicalSpecialty: "https://schema.org/PlasticSurgery",
    },
    {
      "@type": "MedicalProcedure",
      name: "Platelet-rich plasma injection for hair loss",
      description:
        "Preparation of autologous PRP from patient blood and systematic scalp injection in thinning zones under clinical protocol.",
      procedureType: "PercutaneousProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Scalp" },
      url: PAGE_URL,
      medicalSpecialty: "https://schema.org/PlasticSurgery",
    },
    {
      "@type": "FAQPage",
      mainEntity: PRP_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function PRPJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical", href: "/non-surgical-treatments-in-mumbai" },
    { name: "PRP Therapy for Hair Loss", href: "/prp-therapy-hair-loss-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Non-Surgical · Hair</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              PRP Therapy for Hair Loss
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Platelet-Rich Plasma Hair Treatment by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="PRP Hair Therapy" icon="🩸" size="hero"  imageSrc={HERO.prpTherapy} imageAlt="PRP therapy for hair"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is PRP Therapy for Hair Loss?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              PRP, or Platelet-Rich Plasma therapy, is a non-surgical hair treatment where a small amount of the patient&apos;s own blood is processed to concentrate the platelets and growth factors it contains, then injected into areas of the scalp affected by thinning or hair fall. The growth factors in PRP stimulate dormant hair follicles, improve blood supply to the scalp, and increase the thickness and caliber of existing hair.
            </p>
            <p>
              PRP does not create new follicles. It works by improving the function of follicles that are still present but underperforming due to the effects of hair loss. This is why PRP is effective for early and moderate hair thinning where follicles are still alive but producing thinner, shorter hair cycles, and why it is not effective for areas of complete baldness where follicles have been permanently lost.
            </p>
            <p>
              Dr. Vicky Jain provides PRP hair therapy as a standalone treatment for patients in the early stages of hair loss who want to reduce shedding and improve density without surgery, and as an adjunct to hair transplant to improve graft survival and enhance density in the post-transplant period. A customized treatment plan including the number of sessions and maintenance interval is designed for each patient based on their hair loss pattern and degree of thinning.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to PRP Hair Therapy</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                PRP effectiveness depends on the concentration of growth factors in the injected plasma, the areas injected, and the underlying cause of the hair loss. Not all PRP preparations are equivalent. The platelet concentration achieved depends on the centrifugation protocol and the collection system used. Dr. Jain uses a standardized protocol that produces consistent platelet concentrations above the threshold associated with clinical effectiveness. The injection pattern covers the full thinning zone rather than isolated spots, ensuring the growth factor stimulus reaches all affected follicles.
              </p>
              <p>
                PRP is not recommended as a sole treatment for patients whose primary concern is established bald areas. For these patients, hair transplant addresses the structural problem directly and PRP is used as a supportive adjunct. For patients in the early stages of loss who want to preserve existing hair and improve density before significant follicle loss occurs, PRP is an appropriate primary treatment. Dr. Jain makes this distinction clearly during consultation so patients invest in the treatment that actually addresses their specific stage of hair loss.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowPRPWorksSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-prp-works-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">How It Works</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How PRP Therapy Works</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A small amount of blood is drawn from the patient&apos;s arm, identical to a standard blood test. The blood is placed in a specialized collection tube and spun in a centrifuge at a calibrated speed and duration. This separates the blood into its components: red blood cells at the bottom, platelet-poor plasma at the top, and the platelet-rich plasma layer in between. The PRP layer is extracted and prepared for injection.
            </p>
            <p>
              Platelets contain growth factors including PDGF, VEGF, EGF, and IGF that collectively stimulate cell proliferation, increase blood vessel formation in the scalp, extend the hair follicle&apos;s active growth phase, and reduce the inflammation that contributes to follicle miniaturization in androgenetic alopecia. Injected directly into the scalp in areas of thinning, these growth factors act locally on the follicles they contact.
            </p>
            <p>
              The effect builds progressively over multiple sessions. One session produces some biological response but is insufficient to produce meaningful visible improvement. The standard protocol of 3 to 4 monthly sessions establishes a cumulative growth factor stimulus that produces visible results. Maintenance sessions every 4 to 6 months sustain the effect by providing continued growth factor support to follicles that are chronically affected by the underlying cause of loss.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CandidateGridSection() {
  const cards = [
    {
      title: "Early Male Pattern Hair Loss",
      icon: User,
      copy: "Men in the early Norwood grades with a receding hairline or early crown thinning where follicles are still present but producing thinner hair. PRP at this stage slows progression, improves the caliber of thinning hairs, and delays the need for transplant.",
    },
    {
      title: "Female Pattern Hair Thinning",
      icon: Sparkles,
      copy: "Women with diffuse scalp thinning, a widening parting, or reduced overall hair density where follicles are still alive but underperforming. Female pattern hair loss often responds well to PRP because the follicles are present across the scalp and accessible to treatment.",
    },
    {
      title: "Stress-Related Hair Fall",
      icon: Zap,
      copy: "Telogen effluvium triggered by stress, illness, nutritional deficiency, or hormonal change causes diffuse shedding across the scalp. PRP supports follicle recovery and reduces the duration of the shedding phase alongside addressing the underlying cause.",
    },
    {
      title: "Post-Hair Transplant Patients",
      icon: Layers,
      copy: "PRP applied at the time of transplant or in sessions following surgery improves graft survival, accelerates healing, and enhances the density of both transplanted and native surrounding hair. This is one of the most evidence-supported applications of PRP in hair medicine.",
    },
    {
      title: "Patients Wanting to Delay Surgery",
      icon: Clock,
      copy: "Patients who have early thinning, are not yet ready for transplant, or want to preserve existing hair for as long as possible before surgical intervention. PRP is an effective preservation strategy that can meaningfully extend the interval before transplant is needed.",
    },
    {
      title: "Beard and Eyebrow Thinning",
      icon: CircleDot,
      copy: "Patchy beard or thinning eyebrows where follicles are still present but producing sparse growth. PRP injected into these areas improves density and hair caliber in the same way it does on the scalp.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="candidate-grid-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Who Is a Good Candidate for PRP Hair Therapy?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TreatmentProtocolSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="treatment-protocol-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Treatment Plan</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">PRP Treatment Plan and Session Protocol</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The standard initial protocol is 3 to 4 sessions spaced one month apart. This monthly interval allows each session&apos;s growth factor stimulus to take effect before the next session reinforces it. Spacing sessions too closely produces diminishing returns per session. Spacing them too widely loses the cumulative benefit before the next session occurs.
            </p>
            <p>
              After the initial course, maintenance sessions every 4 to 6 months sustain the improvement. The underlying cause of hair loss, whether genetic androgenetic alopecia, hormonal, or stress-related, is ongoing. Without maintenance, the growth factor support diminishes and the hair loss process resumes its natural progression. Patients who commit to the maintenance schedule maintain their improvement significantly longer than those who stop after the initial course.
            </p>
            <p>
              Each session takes 30 to 45 minutes. There is no hospital admission. Patients return to normal activity the same day with minor restrictions for the first 24 to 48 hours. A topical numbing cream is applied before injection to minimize discomfort. Most patients describe the injections as mild pressure rather than pain.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How PRP Therapy is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A small blood sample, approximately 10 to 20 milliliters, is drawn from the patient&apos;s arm at the start of each session. The sample is placed in a specialized PRP collection tube and centrifuged according to the protocol that produces the optimal platelet concentration. The PRP layer is separated and drawn into syringes for injection.
            </p>
            <p>
              The scalp is cleaned and a topical numbing cream is applied to the treatment zone for 15 to 20 minutes before injection. The PRP is then injected into the scalp in a systematic pattern across the full area of thinning, at a depth that places the growth factors in the layer adjacent to the hair follicle bulb. The injection pattern is not random. It is mapped to cover the entire thinning zone evenly.
            </p>
            <p>
              The procedure takes 30 to 45 minutes from blood draw to completion. There is no hospital stay required. Patients leave the clinic the same day. Mild redness and swelling at injection sites typically resolve within a few hours to 24 hours.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultsTimelineSection() {
  const milestones = [
    {
      period: "Month 1",
      description:
        "Reduced hair fall is often the first noticeable change. The active shedding phase slows as follicle support improves. This is the earliest and most consistently reported improvement from PRP.",
      icon: BedDouble,
    },
    {
      period: "Months 2 to 3",
      description:
        "Fine new hairs emerge from stimulated follicles. Hair thickness and caliber begin to improve in the treated areas. Results are subtle at this stage but measurable.",
      icon: PersonStanding,
    },
    {
      period: "Months 4 to 6",
      description:
        "Visible improvement in hair density across the treated zone. Hair feels thicker and the parting or thinning area appears less pronounced. Most patients notice the change in photos from this point.",
      icon: Monitor,
    },
    {
      period: "Months 6 to 12",
      description:
        "Maximum improvement from the initial course is visible. Maintenance sessions begin at 4 to 6 month intervals to sustain the result.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="results-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">PRP Results Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-white/20" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-white/45" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-white mb-2">{milestone.period}</h4>
                  <p className="text-sm text-white/70 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-white/20" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-white/45 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-white mb-2">{milestone.period}</h4>
                  <p className="text-sm text-white/70 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function VsTransplantSection() {
  const rows = [
    {
      label: "Type",
      prp: "Non-surgical. No incisions, no recovery period.",
      transplant: "Surgical. Follicle extraction and implantation procedure.",
    },
    {
      label: "What It Does",
      prp: "Improves the function and density of existing follicles. Does not create new follicles.",
      transplant: "Creates new permanent hair in bald or thinning areas by moving follicles from the donor zone.",
    },
    {
      label: "Effective For",
      prp: "Early thinning where follicles are still present. Not effective for established bald areas without follicles.",
      transplant: "Established bald areas and receded hairlines where follicles are no longer producing hair.",
    },
    {
      label: "Permanence",
      prp: "Requires ongoing maintenance sessions every 4 to 6 months to sustain results.",
      transplant: "Permanent. Transplanted follicles do not fall out.",
    },
    {
      label: "Downtime",
      prp: "Minimal. 24 to 48 hours of minor restrictions.",
      transplant: "5 to 14 days depending on extent of procedure.",
    },
  ];
  const linkClass = "text-gold font-medium hover:text-deep-navy underline underline-offset-2 transition-colors";
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="vs-transplant-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          PRP Therapy vs. Hair Transplant: Which Do You Need?
        </h2>
        <div className="max-w-5xl mx-auto border border-[#E8E2D9] bg-white overflow-hidden">
          <div className="hidden md:grid md:grid-cols-3 gap-0 border-b border-[#E8E2D9] bg-premium-purple text-warm-white">
            <div className="p-4 font-serif text-sm" />
            <div className="p-4 font-serif text-sm border-l border-warm-white/20">PRP Therapy</div>
            <div className="p-4 font-serif text-sm border-l border-warm-white/20">
              <Link to={HAIR_TRANSPLANT_PATH} className="text-warm-white hover:text-gold transition-colors underline-offset-2">
                Hair Transplant
              </Link>
            </div>
          </div>
          {rows.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-[#E8E2D9]"
            >
              <div className="p-4 md:p-5 bg-warm-white/80 font-medium text-deep-navy text-sm md:text-base">{row.label}</div>
              <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="md:hidden font-serif text-premium-purple block mb-2">PRP Therapy</span>
                {row.prp}
              </div>
              <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed">
                <span className="md:hidden font-serif text-premium-purple block mb-2">Hair Transplant</span>
                {row.transplant}
              </div>
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-[#E8E2D9] last:border-b-0">
            <div className="p-4 md:p-5 bg-warm-white/80 font-medium text-deep-navy text-sm md:text-base">Best Used Together</div>
            <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-[#E8E2D9] text-deep-navy/70 text-sm leading-relaxed md:col-span-2">
              <span className="md:hidden font-serif text-premium-purple block mb-2">PRP and Transplant Combined</span>
              <strong className="text-deep-navy font-medium">PRP and Transplant Combined: </strong>
              PRP improves graft survival and enhances native hair density alongside transplant. The combination produces better overall density than either alone.
            </div>
          </div>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-sm md:text-base leading-relaxed text-center md:text-left">
          PRP and{" "}
          <Link to={HAIR_TRANSPLANT_PATH} className={linkClass}>
            hair transplant
          </Link>{" "}
          are not competing options. They serve different stages and different aspects of hair loss. PRP is appropriate when follicles are still present and need support. Transplant is appropriate when follicles are absent and need to be replaced. Many patients benefit from PRP as an early intervention and proceed to transplant when loss has progressed. Post-transplant PRP then supports both the new grafts and the remaining native hair.
        </p>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="PRP" />;

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {PRP_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`prp-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your PRP Hair Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your hair loss pattern, degree of thinning, and scalp condition to determine whether PRP is appropriate for your stage of hair loss and how many sessions are recommended. If transplant is a better fit for your specific concern, that recommendation is made directly. You leave with a clear treatment plan and realistic expectations for what PRP achieves.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button asChild className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          >
            <a href={TEL_URL}>
              <Phone className="w-5 h-5 mr-2" />
              Call {PHONE_DISPLAY}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Hair Transplant",
      description: "Permanent hair restoration for patients with established bald areas beyond what PRP addresses.",
      href: HAIR_TRANSPLANT_PATH,
    },
    {
      name: "Beard Transplant",
      description: "Permanent beard restoration for patients with absent beard follicles where PRP alone is insufficient.",
      href: BEARD_TRANSPLANT_PATH,
    },
    {
      name: "Rhinoplasty",
      description: "Facial balance and proportion for patients addressing multiple aesthetic concerns alongside hair restoration.",
      href: "/rhinoplasty-in-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function PRPTherapyPage() {
  return (
    <>
      <SEO
        title="PRP Therapy for Hair Loss | Dr. Vicky Jain"
        description="Early hair thinning and excessive hair fall without surgery. Dr. Vicky Jain provides PRP hair treatment with customized session plans. Book your consultation."
        canonical="/prp-therapy-hair-loss-mumbai"
      />
      <PRPJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <HowPRPWorksSection />
      <CandidateGridSection />
      <TreatmentProtocolSection />
      <HowPerformedSection />
      <ResultsTimelineSection />
      <VsTransplantSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
